// src/pages/CreateRoom.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../App.css';
import CenterLayout from '../components/CenterLayout';

const departmentOptions = [
  '소프트웨어학과',
  '컴퓨터공학과',
  '디자인컨버전스',
  '신소재공학과'
];
const yearOptions = ['1학년','2학년','3학년','4학년'];
const weekdays = ['월요일','화요일','수요일','목요일','금요일'];

export default function CreateRoom({ user }) {
  const [subject, setSubject] = useState('');
  const [department, setDepartment] = useState('');
  const [year, setYear] = useState('');
  const [day, setDay] = useState(weekdays[0]);
  const [period, setPeriod] = useState('1');
  const [password, setPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCreate = async e => {
    e.preventDefault();
    
    if (!subject.trim()) return alert('과목명을 입력하세요.');
    if (!department) return alert('학과를 선택하세요.');
    if (!year) return alert('학년을 선택하세요.');
    
    try {
      setIsCreating(true);
      
      // Firestore에 방 문서 생성
      const docRef = await addDoc(collection(db, 'rooms'), {
        name: subject.trim(),
        department,
        year,
        subject: subject.trim(),
        day,
        period: Number(period),
        password,
        ownerId: user.id,
        ownerName: user.nickname,
        ownerIconIdx: user.iconIdx ?? 0,
        createdAt: serverTimestamp()
      });

      // 생성 직후 해당 방으로 자동 입장
      navigate(`/rooms/${docRef.id}?pw=${encodeURIComponent(password)}`, { replace: true });
    } catch (error) {
      console.error('방 생성 오류:', error);
      alert('방 생성 중 오류가 발생했습니다.');
      setIsCreating(false);
    }
  };

  return (
    <CenterLayout>
      {/* 헤더 */}
      <div style={{
        marginBottom: '1.5rem',
        position: 'relative',
        background: 'white',
        padding: isMobile ? '1.2rem 1.5rem' : '1.5rem 2rem',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        border: '1px solid #f4f4f8'
      }}>
        <div style={{
          position: 'absolute',
          left: '0',
          top: '0',
          width: '6px',
          height: '100%',
          background: '#4f46e5'
        }}></div>
        <h2 style={{
          margin: 0,
          marginLeft: '6px',
          fontSize: isMobile ? '1.3rem' : '1.5rem',
          fontWeight: 600,
          color: '#111827',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? '36px' : '42px',
            height: isMobile ? '36px' : '42px',
            borderRadius: '12px',
            background: '#f3f4ff',
            color: '#4f46e5'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14"></path>
              <path d="M5 12h14"></path>
            </svg>
          </div>
          새 질문 방 만들기
        </h2>
      </div>
      
      {/* 방 생성 폼 */}
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        padding: '1.5rem',
        marginBottom: isMobile ? '5rem' : '2rem',
        border: '1px solid #f1f5f9',
      }}>
        <form onSubmit={handleCreate}>
          {/* 과목명 입력 */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>과목명 (방 제목)</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="예: 프로그래밍 기초"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
            />
          </div>

          {/* 학과 선택 */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>학과</label>
            <select
              value={department}
              onChange={e => { setDepartment(e.target.value); setYear(''); }}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem',
                paddingRight: '2.5rem',
              }}
            >
              <option value="">학과 선택</option>
              {departmentOptions.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* 학년 선택 */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>학년</label>
            <select
              value={year}
              onChange={e => setYear(e.target.value)}
              disabled={!department}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem',
                paddingRight: '2.5rem',
                opacity: !department ? 0.6 : 1,
              }}
            >
              <option value="">학년 선택</option>
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* 요일/교시 그리드 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            {/* 요일 선택 */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: '#64748b',
              }}>요일</label>
              <select
                value={day}
                onChange={e => setDay(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.95rem',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  backgroundSize: '1rem',
                  paddingRight: '2.5rem',
                }}
              >
                {weekdays.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            {/* 교시 선택 */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: '#64748b',
              }}>교시</label>
              <select
                value={period}
                onChange={e => setPeriod(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.95rem',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  backgroundSize: '1rem',
                  paddingRight: '2.5rem',
                }}
              >
                {Array.from({ length: 9 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>
                    {`${n}교시 (${9 + (n - 1)}:00)`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 비밀번호 입력 */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem',
            }}>
              <label style={{
                fontSize: '0.9rem',
                fontWeight: 500,
                color: '#64748b',
              }}>비밀번호 (선택)</label>
              <div style={{
                fontSize: '0.8rem',
                color: '#94a3b8',
              }}>
                {password ? 
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    <span style={{ color: '#10b981' }}>보호됨</span>
                  </div> : 
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      <line x1="12" y1="15" x2="12" y2="17"/>
                    </svg>
                    <span>누구나 입장 가능</span>
                  </div>
                }
              </div>
            </div>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
              }}
            />
          </div>

          {/* 버튼 영역 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                padding: isMobile ? '0.8rem' : '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #cbd5e1',
                background: 'transparent',
                color: '#64748b',
                fontWeight: '500',
                fontSize: '0.95rem',
                cursor: 'pointer',
                flex: isMobile ? '1 0 100%' : 'initial',
                order: isMobile ? 2 : 1,
                marginBottom: isMobile ? '0' : '0',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isCreating}
              style={{
                padding: isMobile ? '0.8rem' : '0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: '#6366f1',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: isCreating ? 'not-allowed' : 'pointer',
                flex: isMobile ? '1 0 100%' : 1,
                order: isMobile ? 1 : 2,
                marginBottom: isMobile ? '0.5rem' : '0',
                opacity: isCreating ? 0.7 : 1,
              }}
            >
              {isCreating ? '생성 중...' : '방 생성하기'}
            </button>
          </div>
        </form>
      </div>
    </CenterLayout>
  );
}
